import {
  enhancedApi,
  PaymentMethod as PaymentMethodDto,
  useGetVehiclesV2Query,
  Vehicle as VehicleDto,
} from '../../codegen/vehicles';
import { PaymentMethod, Vehicle, VehicleGroup } from '../../models';
import { useAppDispatch, useAppSelector } from '../hooks';
import moment from 'moment';
import { useMemo } from 'react';
import { getSelectedVehicleId } from '../../pages/onboarding/state/onboardingUiSlice';
import { remove } from 'lodash';
import { useCustomIntl } from '../../i18n/i18n';
import { useGetTagsQuery } from '../tags/tagsSlice';

const INDEFINITE_EXPIRY = '12-99';
const EXPIRY_API_FORMAT = 'MM-YY';
const EXPIRY_DOMAIN_FORMAT = 'MM/YYYY';
export const UNASSIGNED_ITEMS_GROUP_ID = 'unassigned';

export const paymentMethodToDomain = (
  paymentMethod: PaymentMethodDto,
): PaymentMethod => {
  const convertedExpiry =
    paymentMethod.expiry === INDEFINITE_EXPIRY
      ? undefined
      : moment(paymentMethod.expiry, EXPIRY_API_FORMAT).format(
          EXPIRY_DOMAIN_FORMAT,
        );

  return {
    ...paymentMethod,
    expiry: convertedExpiry,
  };
};

export const useVehicles = () => {
  const intl = useCustomIntl();
  const vehiclesQuery = useGetVehiclesV2Query();
  const tagsQuery = useGetTagsQuery();

  const vehicleItems = vehiclesQuery?.data?.items || [];

  const unassignedGroup: VehicleGroup = {
    id: UNASSIGNED_ITEMS_GROUP_ID,
    name: intl.formatMessage({ id: 'common.vehicles.unassigned' }),
    position: 'last',
  };

  const groups = useMemo(
    () => [...(tagsQuery.data?.items || []), unassignedGroup],
    [tagsQuery.data],
  );

  const vehicleToDomain = (vehicle: VehicleDto): Vehicle => {
    const paymentMethods = vehicle.paymentMethods.map(paymentMethodToDomain);

    const vehicleGroupIds =
      vehicle.group_ids.length > 0
        ? vehicle.group_ids
        : [UNASSIGNED_ITEMS_GROUP_ID];

    const groupsOfVehicle = vehicleGroupIds
      ?.map((vehicleGroupId) => {
        return groups.find((g) => g.id === vehicleGroupId);
      })
      .filter((g) => !!g);

    return {
      ...vehicle,
      group_ids: vehicleGroupIds,
      groups: groupsOfVehicle,
      paymentMethods,
    };
  };

  const vehicles: Vehicle[] = useMemo(
    () =>
      vehicleItems.map((vehicle: VehicleDto) => {
        return vehicleToDomain(vehicle);
      }),
    [vehicleItems, groups],
  );

  return {
    vehicles,
    groups,
    isLoading:
      vehiclesQuery.isLoading ||
      tagsQuery.isLoading ||
      vehiclesQuery.isFetching ||
      tagsQuery.isFetching,
    isFetching: vehiclesQuery.isFetching || tagsQuery.isFetching,
    error: vehiclesQuery.error || tagsQuery.error,
  };
};

export const useSelectedVehicle = () => {
  const vehicles = useVehicles();
  const selectedVehicleId = useAppSelector(getSelectedVehicleId);
  return useMemo(
    () => vehicles.vehicles.find((vehicle) => vehicle.id === selectedVehicleId),
    [vehicles, selectedVehicleId],
  );
};

export const useLoadedVehicles = () => {
  const dispatch = useAppDispatch();

  const updateAllVehicles = (
    performUpdate: (allVehicles: VehicleDto[]) => VehicleDto[],
  ) => {
    dispatch(
      enhancedApi.util.updateQueryData(
        'getVehiclesV2',
        undefined,
        (draftVehicles?) => {
          if (!draftVehicles) {
            return;
          }

          draftVehicles.items = performUpdate(draftVehicles.items);
        },
      ),
    );
  };

  const updateVehicle = (
    vehicleId: string,
    performUpdate: (vehicle: VehicleDto) => VehicleDto,
  ) => {
    updateAllVehicles((allVehicles) => {
      return allVehicles.map((vehicle) => {
        return vehicle.id === vehicleId ? performUpdate(vehicle) : vehicle;
      });
    });
  };

  const updatePaymentMethod = (
    vehicleId: string,
    paymentMethodId: string,
    performUpdate: (paymentMethod: PaymentMethodDto) => PaymentMethodDto,
  ) => {
    updateVehicle(vehicleId, (vehicle) => {
      vehicle.paymentMethods = vehicle.paymentMethods.map((paymentMethod) => {
        return paymentMethod.id === paymentMethodId
          ? performUpdate(paymentMethod)
          : paymentMethod;
      });
      return vehicle;
    });
  };

  const removePaymentMethod = (vehicleId: string, paymentMethodId: string) => {
    updateVehicle(vehicleId, (vehicle) => {
      removePaymentMethodFromVehicle(vehicle, paymentMethodId);
      removePaymentMethodFromServiceAssignments(vehicle, paymentMethodId);
      return vehicle;
    });
  };

  const removePaymentMethodFromVehicle = (
    vehicle: VehicleDto,
    paymentMethodId: string,
  ) => {
    remove(vehicle.paymentMethods, (pm) => {
      return pm.id === paymentMethodId;
    });
  };

  const removePaymentMethodFromServiceAssignments = (
    vehicle: VehicleDto,
    paymentMethodId: string,
  ) => {
    vehicle.serviceActivations = vehicle.serviceActivations.filter(
      (serviceActivation) =>
        !serviceActivation.paymentMethodAssignments.some(
          (paymentMethodAssignment) =>
            paymentMethodAssignment.paymentMethodId === paymentMethodId,
        ),
    );
  };

  return {
    updateVehicle,
    updatePaymentMethod,
    removePaymentMethod,
  };
};
